import { createElement, type ComponentType, type ReactNode } from "react";

import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";
import type { ScoutToolPart } from "./message/scoutChatMessage";

import {
  LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND,
  mapAwaitingConfirmationDocumentsToLargeFileProposal,
  type ScoutLargeFileFullAnalysisProposal,
} from "./scoutLargeFileFullAnalysisProposal";
import {
  isScoutTenantDocumentProposalTool,
  parseScoutTenantDocumentProposal,
  TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND,
  type ScoutTenantDocumentProposal,
} from "./scoutTenantDocumentProposal";
import { ScoutTenantDocumentProposalCard } from "./ScoutTenantDocumentProposalCard";

/**
 * Proposal kinds that Oak can resolve via the shared HITL protocol.
 * Matches `ScoutUserConfirmationProposalKind` in encamp-scout/core; Oak
 * keeps its own copy so the UI package does not depend on scout-core.
 */
export type ScoutProposalKind =
  | typeof TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND
  | typeof LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND;

/**
 * Tool-result discovery entry: scan assistant tool parts, parse into a card
 * view model, render the kind's Card under the tool group.
 */
export type ScoutToolResultProposalRegistryEntry<TProposal> = {
  source: "tool_result";
  kind: ScoutProposalKind;
  matchesToolPart: (toolPart: ScoutToolPart) => boolean;
  parseProposal: (toolOutput: unknown) => TProposal | null;
  Card: ComponentType<{ proposal: TProposal }>;
};

/**
 * Domain-status discovery entry: derive pending proposals from durable
 * domain state (e.g. `PrivateDocument.extractionStatus`), not tool output.
 * UI surface is kind-specific — large-file keeps composer-chrome buttons
 * rather than an inline card.
 */
export type ScoutDomainStatusProposalRegistryEntry = {
  source: "domain_status";
  kind: ScoutProposalKind;
  /**
   * Map conversation domain rows into a proposal view model, or null when
   * nothing is awaiting confirmation for this kind.
   */
  discoverFromDocuments: (
    documents: ReadonlyArray<ScoutConversationPrivateDocumentRow>
  ) => ScoutLargeFileFullAnalysisProposal | null;
};

/**
 * Kind → card + tool-result discovery. Confirm/decline mutations live in
 * `ScoutProposalContext` (runtime-bound); this table owns the static UI +
 * parse side. Add a row when wiring a new tool-emitted approval kind.
 */
export const SCOUT_TOOL_RESULT_PROPOSAL_REGISTRY = [
  {
    source: "tool_result",
    kind: TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND,
    matchesToolPart: isScoutTenantDocumentProposalTool,
    parseProposal: parseScoutTenantDocumentProposal,
    Card: ScoutTenantDocumentProposalCard,
  },
] as const satisfies ReadonlyArray<
  ScoutToolResultProposalRegistryEntry<ScoutTenantDocumentProposal>
>;

/**
 * Kind → domain-status discovery. No Card — each kind owns its surface
 * (large-file: `ScoutLargeDocumentOptInButtons` in ChatFooter). Add a row
 * when wiring a new DB/status-driven approval kind.
 *
 * To add a kind: (1) shared proposal shape in encamp-scout/core, (2) Oak
 * mapper + synthetic-event helpers, (3) handlers in ScoutProposalContext,
 * (4) registry row here, (5) wire discover + resolve at the host surface.
 */
export const SCOUT_DOMAIN_STATUS_PROPOSAL_REGISTRY = [
  {
    source: "domain_status",
    kind: LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND,
    discoverFromDocuments: mapAwaitingConfirmationDocumentsToLargeFileProposal,
  },
] as const satisfies ReadonlyArray<ScoutDomainStatusProposalRegistryEntry>;

/**
 * Render approval cards for every recognized awaiting-confirmation tool
 * result in a tool group. Unknown tools are skipped.
 */
export function renderScoutProposalCardsFromToolParts(tools: ScoutToolPart[]): ReactNode[] {
  const cards: ReactNode[] = [];
  for (const toolPart of tools) {
    for (const entry of SCOUT_TOOL_RESULT_PROPOSAL_REGISTRY) {
      if (!entry.matchesToolPart(toolPart)) continue;
      const proposal = entry.parseProposal(toolPart.toolOutput);
      if (!proposal) continue;
      cards.push(
        createElement(entry.Card, {
          key: `${toolPart.id}-proposal-card`,
          proposal,
        })
      );
    }
  }
  return cards;
}

/**
 * Discover the pending large-file full-analysis batch from conversation
 * private-document rows (domain-status source).
 */
export function discoverLargeFileFullAnalysisProposal(
  documents: ReadonlyArray<ScoutConversationPrivateDocumentRow>
): ScoutLargeFileFullAnalysisProposal | null {
  const entry = SCOUT_DOMAIN_STATUS_PROPOSAL_REGISTRY.find(
    (candidate) => candidate.kind === LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND
  );
  return entry?.discoverFromDocuments(documents) ?? null;
}
